import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import queryString from 'query-string';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import allActions from '../../store/actions';
import {
  getTranslation,
  getLocalStorageValuesKey,
} from '../../utils/functions';

import { SERVER_ERRORS } from '../../constants/backendResponse';
import {
  FORM_SESSION_TOKEN_PARAM,
  PARAM_PREFIX,
  FORM_ID_PREVIEW_PARAM,
  ORGANISATION_FORM_SESSION_TOKEN_PARAM,
  PROJECTID_PARAM,
  FORMID_PARAM,
  TENANTID_PARAM,
  EVENTID_PARAM,
} from '../../constants/formParams';
import ServerError from '../ServerError/ServerError';
import {
  appConfigLoadStatusSelector,
  appErrorSelector,
  serverErrorMessageSelector,
} from '../../store/selectors/appSelectors';
import { projectIdSelector, tenantIdSelector } from '../../store/selectors/formSessionSelectors';
import { fieldsSelector } from '../../store/selectors/formSessionSelectors';
import {
  formDefIdSelector,
  headerSelector,
} from '../../store/selectors/globalConfigSelectors';
import LoginFlowModal from '../LoginFlowModal/LoginFlowModal';
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from '../../utils/sessionUtils';
import { isAuthenticated } from '../../utils/authService';
import { validationResponseSelector } from '../../store/selectors/formConfigSelectors';
import { useHistory } from 'react-router-dom';
import buildPATHS from '../../utils/paths';
import {
  continueAsGuestSelector,
  createAccountEnabledSelector,
  loginEnabledSelector,
} from '../../store/selectors/formSessionSelectors';
import AppError from '../AppError/AppError';

const PAGE_TO_SHOW = {
  FORM_NOT_OPENED: 'formNotOpened',
  FORM_EXPIRED: 'formExpired',
  FORM_VALID: 'formValid',
};
const InitialApp = ({ children }) => {
  const {
    appConfigLoadStatus,
    serverErrorMessage,
    backendInitialValues,
    header,
    formDefId,
    validationResponse,
    appError,
    continueAsGuest,
    loginEnabled,
    createAccountEnabled,
  } = useSelector((state) => ({
    // app state
    appConfigLoadStatus: appConfigLoadStatusSelector(state),
    serverErrorMessage: serverErrorMessageSelector(state),
    // formSession state
    backendInitialValues: fieldsSelector(state),
    // globalConfig state
    header: headerSelector(state),
    formDefId: formDefIdSelector(state),
    validationResponse: validationResponseSelector(state),
    appError: appErrorSelector(state),
    continueAsGuest: continueAsGuestSelector(state),
    loginEnabled: loginEnabledSelector(state),
    createAccountEnabled: createAccountEnabledSelector(state),
    projectId: projectIdSelector(state),
    tenantId: tenantIdSelector(state),
  }));

  const { projectId, tenantId } = useSelector((state) => ({
    projectId: projectIdSelector(state),
    tenantId: tenantIdSelector(state),
  }));

  const dispatch = useDispatch();
  const { search } = useLocation();
  const [showLoginFlowModal, setShowLoginFlowModal] = useState(true);
  // const [showPage, setShowPage] = useState(PAGE_TO_SHOW.FORM_VALID);
  const history = useHistory();
  const PATHS = buildPATHS();

  //   useEffect(() => {
  // if(validationResponse && validationResponse.startTimeValidationFailed) {
  //   setShowPage(PAGE_TO_SHOW.FORM_NOT_OPENED);
  // } else if(validationResponse && validationResponse.endTimeValidationFailed) {
  //   history.push(PATHS.page_expired)
  //   setShowPage(PAGE_TO_SHOW.FORM_EXPIRED);
  // }
  //   }, [validationResponse]);

  const saveURLparams = () =>
    dispatch(allActions.appConfigActions.setURLparams(search));

  const getInitialValuesFromLocalStorage = () => {
    return (
      JSON.parse(localStorage.getItem(getLocalStorageValuesKey(formDefId))) ||
      {}
    );
  };

  const getInitialValuesFromUrl = () => {
    return Object.entries(queryString.parse(search)).reduce((acc, entry) => {
      const [key, value] = entry;
      if (!key.includes(PARAM_PREFIX)) {
        return acc;
      }
      return {
        ...acc,
        [key.slice(PARAM_PREFIX.length)]: value,
      };
    }, {});
  };

  const checkInitialValues = () => {
    const localStorageInitialValues = getInitialValuesFromLocalStorage();
    const urlInitialValues = getInitialValuesFromUrl();

    const mapBackendInitialValues = (values) => {
      if (!values) return {};
      return values.reduce(
        (acc, { fieldCode, value }) => ({ ...acc, [fieldCode]: value }),
        {},
      );
    };
    dispatch(
      allActions.appConfigActions.setFormInitialValues({
        ...localStorageInitialValues,
        ...urlInitialValues,
        ...mapBackendInitialValues(backendInitialValues),
      }),
    );
  };

  const initApp = () => {
    const formSessionToken = queryString.parse(window.location.search)[
      FORM_SESSION_TOKEN_PARAM
    ];
    const formIdPreview = queryString.parse(window.location.search)[
      FORM_ID_PREVIEW_PARAM
    ];
    const organisationFormSessionToken = queryString.parse(
      window.location.search,
    )[ORGANISATION_FORM_SESSION_TOKEN_PARAM];
    const projectId = queryString.parse(window.location.search)[
      PROJECTID_PARAM
    ];

    let appConfig;

    // WRONG URL
    if (
      !formSessionToken &&
      !formIdPreview &&
      !organisationFormSessionToken &&
      !projectId
    ) {
      dispatch(
        allActions.appConfigActions.setServerError(SERVER_ERRORS.NOT_FOUND),
      );
      return;
    }

    // PREVIEW FORM - WITH PREFILLED DATA
    if (formIdPreview) {
      appConfig = {
        tenantId: formIdPreview.split('-').shift(),
        formId: formIdPreview.split('-').slice(1).join('-'),
        preview: true,
      };
    }

    // ACCREDITATION FORM
    if (formSessionToken) {
      const tenantIdFromToken = formSessionToken.split('-').shift();
      const formSessionFromToken = formSessionToken
        .split('-')
        .slice(1)
        .join('-');

      appConfig = {
        formSessionToken: formSessionFromToken,
        tenantId: tenantIdFromToken,
      };
    }

    // ACCREDITATION ORGANISATION FORM
    if (organisationFormSessionToken) {
      const tenantIdFromToken = organisationFormSessionToken.split('-').shift();
      const organisationFormSessionFromToken = organisationFormSessionToken
        .split('-')
        .slice(1)
        .join('-');

      appConfig = {
        organisationFormSessionToken: organisationFormSessionFromToken,
        tenantId: tenantIdFromToken,
      };
    }

    // Registration generic FORM
    if (projectId) {
      const formId = queryString.parse(window.location.search)[FORMID_PARAM];
      const tenantId = queryString.parse(window.location.search)[
        TENANTID_PARAM
      ];
      const eventId = queryString.parse(window.location.search)[EVENTID_PARAM];

      appConfig = {
        formDefId: formId,
        projectId,
        tenantId,
        eventId,
      };
    }

    dispatch(
      allActions.appConfigActions.getAppConfigAction(appConfig, history),
    );
  };

  const styles = {
    width: '100vw',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    // justifyContent: 'center',
    marginTop: '128px',
    alignItems: 'center',
  };

  const isOnlyLoginEnabled = () => {
    return loginEnabled && !continueAsGuest && !createAccountEnabled;
  };

  useEffect(() => {
    if (isOnlyLoginEnabled() && !isAuthenticated()) {
      history.push(PATHS.login);
    }

    if (isAuthenticated() && !getSessionStorageItem('isLoginFlowModalClosed')) {
      setSessionStorageItem('isLoginFlowModalClosed', true);
    }
  }, [loginEnabled, continueAsGuest, createAccountEnabled]);

  useEffect(() => {
    saveURLparams();

    if (appConfigLoadStatus) {
      checkInitialValues();
    } else {
      initApp();
    }
  }, [dispatch, appConfigLoadStatus]);

  const showLoginFlowPopup = () => {
    return (
      !isAuthenticated() &&
      !appError &&
      !isOnlyContinueAsGuestEnabled() &&
      !isOnlyLoginEnabled() &&
      !getSessionStorageItem('isLoginFlowModalClosed') && (
        <LoginFlowModal onClose={() => setShowLoginFlowModal(false)} />
      )
    );
  };

  const isOnlyContinueAsGuestEnabled = () => {
    return continueAsGuest && !loginEnabled && !createAccountEnabled;
  };

  const isOnlyCreateAccount = () => {
    return createAccountEnabled && !continueAsGuest && !loginEnabled;
  };
  if (appConfigLoadStatus) {
    return (
      <>
        <Helmet>
          <title>{getTranslation(header.pageTitle)}</title>
          <meta name="description" content={getTranslation(header.pageTitle)} />
          <link rel="apple-touch-icon" href={header.faviconUrl} />
          <link rel="icon" href={header.faviconUrl} />
        </Helmet>
        <>
          {/* {showPage === PAGE_TO_SHOW.FORM_NOT_OPENED && (
           <FormNotAvailableModal validationData={validationResponse} />
        )} */}
          {showLoginFlowPopup()}
          {children}
        </>
      </>
    );
  }

  const renderErrorPopup = () => {
    return (
      <>
        {serverErrorMessage ? <ServerError /> : appError ? <AppError /> : null}
      </>
    );
  };
  return (
    <div style={styles}>
      {serverErrorMessage ? (
        renderErrorPopup()
      ) : (
        <CircularProgress style={{ color: 'rgba(0,0,0,0.87)' }} />
      )}
    </div>
  );
};

// InitialApp.propTypes = {
//   children: PropTypes.element.isRequired,
// };

export default InitialApp;
